import * as React from 'react';

import ImgixLib from 'js/lib/imgix';

import { SvgArrowRight, SvgStarFilled } from '@coursera/coursera-ui/svg';

import type { CareerPlansCourseCardType } from 'bundles/career-plans/types/career-plans-course-card';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import { getS12nVariantLabel } from 'bundles/s12n-common/lib/s12nProductVariantUtils';

import _t from 'i18n!nls/career-plans';

import 'css!./__styles__/CareerPlansCourseCardMobile';

type Props = CareerPlansCourseCardType;

const careerPlansCourseCard = ({
  id,
  careerName,
  name,
  description,
  descriptionOverride,
  imageUrl,
  partners = [],
  averageFiveStarRating,
  ratingCount,
  type,
  objectUrl,
  courseCount,
  isCourse,
  isProject,
  s12nProductVariant,
  isFoundational = false,
  loaded = false,
  positionIndex,
  careerPosition,
  computedLevel,
}: Props) => {
  if (!loaded || !name || !objectUrl) return null;

  /*
    computedType and computedDescription are being built here due to the necessity for supporting both 
    static and dynamic content. 
    
    With static content, we're free to build out descriptions however we like, but we're limited to what's 
    returned from our APIs with dynamic content without an intentional alternative. descriptionOverride is
    populated by users on contentful.

    Types are explicitly stated due to how we built out contentful models, so it seems easier to pull from 
    that than to destructure a response.
  */
  let computedType = type;
  if (isProject) {
    computedType = _t('Guided Project');
  } else if (s12nProductVariant) {
    computedType = getS12nVariantLabel(s12nProductVariant);
  } else if (isCourse) {
    computedType = _t('Course');
  }

  const computedDescription = descriptionOverride?.length ? descriptionOverride : description;

  const onClickHandler = (event: $TSFixMe) => {
    event.preventDefault();
    window.open(objectUrl, '_blank');
    return null;
  };

  const CareerPlansCourseCardImage = () => {
    let altTagContent = name;

    if (partners?.length > 0) {
      altTagContent += ` by ${partners[0]}`;
    }
    // we're using imgixLib here because imgix component caused render loop while using CUI Collapse
    const src = ImgixLib.processImage(imageUrl, { fit: 'fill', auto: 'format,compress', bg: 'FFF' });

    const ImageComponent = (
      <img src={src} alt={altTagContent} width={100} height={100} className="careerPlansCourseCardMobile-image" />
    );

    return <div className="careerPlansCourseCardMobile-imageContainer">{ImageComponent}</div>;
  };

  const CareerPlansCourseCardLink = () => (
    <TrackedA
      trackingName="careerplans_mobile_course_link"
      className="careerPlansCourseCardMobile-link"
      href={objectUrl}
      data={{
        entityid: id,
        entityindex: positionIndex,
        listidentifier: careerName,
        listindex: careerPosition,
        subtype: computedLevel,
      }}
      withVisibilityTracking={true}
      requireFullyVisible={false}
    >
      {_t('Go to #{type}', { type: computedType })}
      <SvgArrowRight size={18} color="var(--cds-color-blue-600)" hoverColor="var(--cds-color-blue-700)" />
    </TrackedA>
  );

  const CareerPlansCourseCardType = () => (
    <div className="careerPlansCourseCardMobile-type careerPlans-sectionHeader">
      {computedType}
      {courseCount && courseCount > 1 && ` (${courseCount} Courses)`}
    </div>
  );

  const CareerPlansCourseCardRating = () => (
    <div className="careerPlansCourseCardMobile-metaData--ratings">
      <span className="careerPlansCourseCardMobile-metaData--star">
        <SvgStarFilled size={14} color="var(--cds-color-yellow-200)" />
      </span>
      <span className="careerPlansCourseCardMobile-metaData--rating">{averageFiveStarRating}</span>
      {!!ratingCount && (
        <span
          className="careerPlansCourseCardMobile-metaData--ratingCount"
          data-e2e="CareerPlansProductCard-rating--count"
        >
          {_t('#{ratingCount} Ratings', { ratingCount: ratingCount?.toLocaleString() })}
        </span>
      )}
    </div>
  );

  const cardButtonTrackingName = `careerplans_mobile_course_card`;
  const cardButtonId = `${cardButtonTrackingName}~${careerName}~${name.replace(/\s+/g, '_')}`;

  return (
    <section className="rc-CareerPlansCourseCardMobile" data-e2e="CareerPlansProductCardMobile">
      <TrackedButton
        id={cardButtonId}
        trackingName={cardButtonTrackingName}
        className="careerPlansCourseCardMobile-button"
        onClick={onClickHandler}
        data={{
          entityid: id,
          entityindex: positionIndex,
          listidentifier: careerName,
          listindex: careerPosition,
          subtype: computedLevel,
        }}
        withVisibilityTracking={true}
        requireFullyVisible={false}
      >
        <div className="careerCard-container is-bottom">
          <div className="careerPlansCourseCardMobile-container--header">
            <div className="careerPlansCourseCardMobile-container--left">
              <div className="careerPlansCourseCardMobile-name" data-e2e="CareerPlansProductCard-name">
                {isFoundational && (
                  <span
                    className="careerPlansCourseCardMobile-name--tag"
                    data-e2e="CareerPlansProductCard-foundationalTag"
                  >
                    {_t('Foundational Learning')}
                  </span>
                )}
                {name}
              </div>
              <div className="careerPlansCourseCardMobile-metaData">
                {computedType && <CareerPlansCourseCardType data-e2e="CareerPlansProductCard-type" />}
                {averageFiveStarRating && <CareerPlansCourseCardRating data-e2e="CareerPlansProductCard-rating" />}
              </div>
            </div>
            <div className="careerPlansCourseCardMobile-container--right">
              {imageUrl && <CareerPlansCourseCardImage data-e2e="CareerPlansProductCard-image" />}
            </div>
          </div>

          {computedDescription && (
            <div
              className="careerPlansCourseCardMobile-description careerPlans-text"
              data-e2e="CareerPlansProductCard-description"
            >
              {computedDescription}
            </div>
          )}
          {objectUrl && computedType && <CareerPlansCourseCardLink data-e2e="CareerPlansProductCard-link" />}
          <div className="border-bottom" />
        </div>
      </TrackedButton>
    </section>
  );
};

export default careerPlansCourseCard;
